import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  Alert,
  CircularProgress,
  Button,
  Switch,
  FormControlLabel,
  TextField,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  useMediaQuery,
} from '@mui/material';
import {
  Save,
  Check,
  ExpandMore,
  Add,
  Delete,
  Refresh,
} from '@mui/icons-material';
import { toast } from 'sonner';
import Config from '../config/constants';

// 토큰 가져오기 헬퍼 함수
const getAuthToken = (): string | null => {
  try {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const user = JSON.parse(storedUser);
      return user.accessToken || null;
    }
  } catch (error) {
    console.error('토큰 가져오기 오류:', error);
  }
  return null;
};

interface GPTPrompt {
  id: string;
  type: 'diary' | 'promotion';
  title: string;
  content: string;
  isActive: boolean;
  branchId: number;
  createdAt: string;
  updatedAt: string;
}

interface GPTSettings {
  id?: string;
  branchId: number;
  defaultPromptType: 'diary' | 'promotion' | 'custom';
  defaultDiaryPrompt?: string;
  defaultPromotionPrompt?: string;
  instagramUrl?: string;
  customGuideline?: string;
}

interface GPTCustomPrompt {
  id?: string;
  branchId: number;
  title: string;
  content: string;
  guideline?: string;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface GPTPromptSettingsProps {
  branchId: number;
}

const GPTPromptSettings: React.FC<GPTPromptSettingsProps> = ({ branchId }) => {
  const [prompts, setPrompts] = useState<GPTPrompt[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedType, setSelectedType] = useState<
    'diary' | 'promotion' | 'all'
  >('all');
  const [defaultDiaryPrompt, setDefaultDiaryPrompt] = useState<string>('');
  const [defaultPromotionPrompt, setDefaultPromotionPrompt] =
    useState<string>('');
  const [defaultPromptType, setDefaultPromptType] = useState<
    'diary' | 'promotion' | string
  >('diary');
  const [instagramUrl, setInstagramUrl] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [newPromptTitle, setNewPromptTitle] = useState<string>('');
  const [newPromptContent, setNewPromptContent] = useState<string>('');
  const [newPromptType, setNewPromptType] = useState<'diary' | 'promotion'>(
    'diary',
  );
  const [customGuideline, setCustomGuideline] = useState<string>('');

  // 커스텀 AI 설정 관련 상태
  const [customPrompts, setCustomPrompts] = useState<GPTCustomPrompt[]>([]);
  const [customPromptTitle, setCustomPromptTitle] = useState<string>('');
  const [customPromptContent, setCustomPromptContent] = useState<string>('');
  const [customPromptGuideline, setCustomPromptGuideline] =
    useState<string>('');

  // 새로운 커스텀 AI 만들기 UI 표시 여부
  const [showCustomPromptForm, setShowCustomPromptForm] =
    useState<boolean>(false);

  const [editCustomTitle, setEditCustomTitle] = useState<string>('');
  const [editCustomContent, setEditCustomContent] = useState<string>('');
  const [editCustomGuideline, setEditCustomGuideline] = useState<string>('');
  const [editCustomIsActive, setEditCustomIsActive] = useState<boolean>(true);
  const [editDirty, setEditDirty] = useState<boolean>(false);

  const isMobile = useMediaQuery('(max-width:600px)');

  // 데이터 초기화 및 로드
  useEffect(() => {
    if (branchId) {
      loadSettings();
      fetchPrompts();
      fetchCustomPrompts();
    }
  }, [branchId, selectedType]);

  const selectedCustomId = useMemo(() => {
    return defaultPromptType.startsWith('custom_')
      ? defaultPromptType.replace(/^custom_/, '')
      : null;
  }, [defaultPromptType]);

  // 선택된 커스텀 프롬프트 객체
  const selectedCustomPrompt = useMemo(() => {
    if (!selectedCustomId) return undefined;
    return customPrompts.find((p) => String(p.id) === String(selectedCustomId));
  }, [customPrompts, selectedCustomId]);

  // 선택이 변경되면 편집폼 초기값 세팅
  useEffect(() => {
    if (selectedCustomPrompt) {
      setEditCustomTitle(selectedCustomPrompt.title || '');
      setEditCustomContent(selectedCustomPrompt.content || '');
      setEditCustomGuideline(selectedCustomPrompt.guideline || '');
      setEditCustomIsActive(
        typeof selectedCustomPrompt.isActive === 'boolean'
          ? selectedCustomPrompt.isActive
          : true,
      );
      setEditDirty(false);
    }
  }, [selectedCustomPrompt]);

  // 설정 로드
  const loadSettings = async () => {
    try {
      const token = getAuthToken();
      if (!token) {
        toast.error('인증 토큰이 없습니다.');
        return;
      }

      const response = await fetch(
        `${Config.API_HOST}/api/gpt/settings/${branchId}`,
        {
          headers: {
            access_token: token,
          },
        },
      );

      if (!response.ok) {
        throw new Error(`설정 로드 실패: ${response.status}`);
      }

      const data = await response.json();

      if (data.success && data.data) {
        const { settings } = data.data;

        // 기본 설정 로드
        if (settings) {
          const promptType = settings.defaultPromptType || 'diary';
          setDefaultPromptType(promptType);
          setDefaultDiaryPrompt(settings.defaultDiaryPrompt || '');
          setDefaultPromotionPrompt(settings.defaultPromotionPrompt || '');
          setInstagramUrl(settings.instagramUrl || '');
          setCustomGuideline(settings.customGuideline || '');

          // localStorage에도 저장
          localStorage.setItem(`defaultPromptType_${branchId}`, promptType);
          console.log('✅ 설정 로드 시 localStorage에 저장:', {
            branchId,
            promptType,
          });
        }
      }
    } catch (error: any) {
      console.error('설정 로드 오류:', error);
      // 에러가 나도 계속 진행할 수 있도록 토스트만 표시
      toast.error(`설정 로드 실패: ${error.message}`);
    }
  };

  // 커스텀 프롬포트 목록 로드
  const fetchCustomPrompts = async () => {
    try {
      const token = getAuthToken();
      if (!token) return;

      console.log('🔍 커스텀 프롬포트 조회 시작:', { branchId });

      const response = await fetch(
        `${Config.API_HOST}/api/gpt/custom-prompts/${branchId}`,
        {
          headers: {
            access_token: token,
          },
        },
      );

      if (!response.ok) {
        throw new Error(`커스텀 프롬포트 조회 실패: ${response.status}`);
      }

      const data = await response.json();
      console.log('📋 커스텀 프롬포트 조회 응답:', data);

      if (data.success) {
        setCustomPrompts(data.data || []);
        console.log(
          '✅ 커스텀 프롬포트 설정 완료:',
          data.data?.length || 0,
          '개',
        );
      }
    } catch (error: any) {
      console.error('❌ 커스텀 프롬포트 조회 오류:', error);
    }
  };

  const fetchPrompts = async () => {
    setLoading(true);
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('인증 토큰이 없습니다.');
      }

      const apiUrl = `${Config.API_HOST}/api/gpt/prompts/${branchId}`;
      const url =
        selectedType !== 'all' ? `${apiUrl}?type=${selectedType}` : apiUrl;

      const response = await fetch(url, {
        headers: {
          access_token: token,
        },
      });

      if (!response.ok) {
        throw new Error(
          `HTTP ${response.status}: 프롬프트 조회에 실패했습니다.`,
        );
      }

      const data = await response.json();
      setPrompts(data.data || []);
    } catch (error: any) {
      console.error('프롬프트 조회 오류:', error);
      toast.error(`프롬프트 조회 실패: ${error.message || '알 수 없는 오류'}`);
    } finally {
      setLoading(false);
    }
  };

  // Select 값 변경 핸들러
  const handlePromptTypeChange = (value: string) => {
    if (value === 'create_new_custom') {
      // "새로운 커스텀 AI 설정 만들기" 선택
      setShowCustomPromptForm(true);
      setDefaultPromptType(defaultPromptType); // 기존 값 유지
    } else {
      // 일반적인 선택
      setDefaultPromptType(value);
      setShowCustomPromptForm(false);
      setEditDirty(false);

      // localStorage에도 즉시 저장
      localStorage.setItem(`defaultPromptType_${branchId}`, value);
      console.log('✅ 프롬프트 타입 변경 시 localStorage 저장:', {
        branchId,
        promptType: value,
      });
    }
  };

  // Instagram URL 유효성 검사
  const isValidInstagramUrl = (url: string): boolean => {
    const instagramUrlPattern =
      /^https:\/\/(www\.)?instagram\.com\/[a-zA-Z0-9._-]+\/?$/;
    return url === '' || instagramUrlPattern.test(url);
  };

  // 기본 프롬프트 설정 저장
  const saveDefaultPrompts = async () => {
    setSaving(true);
    try {
      if (!isValidInstagramUrl(instagramUrl)) {
        throw new Error(
          '유효한 Instagram URL을 입력해주세요 (예: https://www.instagram.com/username/).',
        );
      }

      const token = getAuthToken();
      if (!token) {
        throw new Error('인증 토큰이 없습니다.');
      }

      const response = await fetch(
        `${Config.API_HOST}/api/gpt/settings/${branchId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            access_token: token,
          },
          body: JSON.stringify({
            defaultPromptType,
            defaultDiaryPrompt,
            defaultPromotionPrompt,
            instagramUrl,
            customGuideline,
          }),
        },
      );

      if (!response.ok) {
        throw new Error(`설정 저장 실패: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        // localStorage에도 저장
        localStorage.setItem(
          `defaultPromptType_${branchId}`,
          defaultPromptType,
        );
        console.log('✅ localStorage에 defaultPromptType 저장:', {
          branchId,
          defaultPromptType,
        });

        toast.success('기본 프롬프트 및 설정이 저장되었습니다!');
      } else {
        throw new Error(data.message || '저장에 실패했습니다.');
      }
    } catch (error: any) {
      console.error('설정 저장 오류:', error);
      toast.error(`설정 저장 실패: ${error.message || '알 수 없는 오류'}`);
    } finally {
      setSaving(false);
    }
  };

  // 커스텀 AI 설정 저장
  const saveCustomPrompt = async () => {
    setSaving(true);
    try {
      if (!customPromptTitle.trim()) {
        throw new Error('커스텀 프롬프트 제목을 입력해주세요.');
      }
      if (!customPromptContent.trim()) {
        throw new Error('커스텀 프롬프트 내용을 입력해주세요.');
      }

      const token = getAuthToken();
      if (!token) {
        throw new Error('인증 토큰이 없습니다.');
      }

      console.log('💾 커스텀 AI 저장 시작:', {
        branchId,
        title: customPromptTitle,
        contentLength: customPromptContent.length,
      });

      const response = await fetch(
        `${Config.API_HOST}/api/gpt/custom-prompt/${branchId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            access_token: token,
          },
          body: JSON.stringify({
            title: customPromptTitle,
            content: customPromptContent,
            guideline: customPromptGuideline,
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `커스텀 프롬프트 저장 실패: ${response.status}`,
        );
      }

      const data = await response.json();
      console.log('📤 커스텀 AI 저장 응답:', data);

      if (data.success) {
        console.log('✅ 커스텀 AI 저장 성공:', data.data);

        // 새로 생성된 커스텀 AI를 선택
        const newCustomPromptId = `custom_${data.data.id}`;
        console.log('🎯 새로운 커스텀 AI ID:', newCustomPromptId);
        setDefaultPromptType(newCustomPromptId);

        // localStorage에도 저장
        localStorage.setItem(
          `defaultPromptType_${branchId}`,
          newCustomPromptId,
        );
        console.log('✅ localStorage에 커스텀 AI ID 저장:', {
          branchId,
          promptId: newCustomPromptId,
        });

        // 폼 초기화 및 숨기기
        setCustomPromptTitle('');
        setCustomPromptContent('');
        setCustomPromptGuideline('');
        setShowCustomPromptForm(false);

        // 커스텀 프롬포트 목록 새로고침
        console.log('🔄 커스텀 프롬포트 목록 새로고침 시작...');
        await fetchCustomPrompts();

        toast.success('커스텀 AI 설정이 저장되고 선택되었습니다!');
      } else {
        throw new Error(data.message || '저장에 실패했습니다.');
      }
    } catch (error: any) {
      console.error('❌ 커스텀 설정 저장 오류:', error);
      toast.error(
        `커스텀 설정 저장 실패: ${error.message || '알 수 없는 오류'}`,
      );
    } finally {
      setSaving(false);
    }
  };

  // 커스텀 AI 설정 삭제
  const deleteCustomPrompt = async (promptId: string) => {
    if (!window.confirm('정말로 이 커스텀 AI 설정을 삭제하시겠습니까?')) {
      return;
    }

    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('인증 토큰이 없습니다.');
      }

      const response = await fetch(
        `${Config.API_HOST}/api/gpt/custom-prompt/${promptId}`,
        {
          method: 'DELETE',
          headers: {
            access_token: token,
          },
        },
      );

      if (!response.ok) {
        throw new Error(`삭제 실패: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        // 커스텀 프롬포트 목록 새로고침
        await fetchCustomPrompts();

        // 현재 선택된 타입이 삭제된 커스텀 프롬포트라면 기본값으로 변경
        if (defaultPromptType === `custom_${promptId}`) {
          setDefaultPromptType('diary');
          localStorage.setItem(`defaultPromptType_${branchId}`, 'diary');
          console.log('✅ 커스텀 AI 삭제 시 localStorage 업데이트:', {
            branchId,
            promptType: 'diary',
          });
        }

        toast.success('커스텀 AI 설정이 삭제되었습니다!');
      } else {
        throw new Error(data.message || '삭제에 실패했습니다.');
      }
    } catch (error: any) {
      console.error('커스텀 설정 삭제 오류:', error);
      toast.error(
        `커스텀 설정 삭제 실패: ${error.message || '알 수 없는 오류'}`,
      );
    }
  };
  const extractNumericId = (raw: string | number | null | undefined): string | null => {
    if (raw === null || raw === undefined) return null;
    const s = String(raw);
    const m = s.match(/(\d+)$/); // 끝에 있는 숫자만 캡쳐
    return m ? m[1] : null;
  };
  
  const saveCustomPromptEdits = async () => {
    // selectedCustomId가 'custom_5'일 수도 있으니 숫자만 추출
    const numericId = extractNumericId(selectedCustomId);
    if (!numericId) {
      toast.error('선택된 커스텀 AI의 ID를 찾을 수 없습니다.');
      return;
    }
  
    if (!editCustomTitle.trim()) {
      toast.error('제목을 입력해주세요.');
      return;
    }
    if (!editCustomContent.trim()) {
      toast.error('내용을 입력해주세요.');
      return;
    }
  
    setSaving(true);
    try {
      const token = getAuthToken();
      if (!token) throw new Error('인증 토큰이 없습니다.');
  
      const apiBase = Config.API_HOST;
      const res = await fetch(`${apiBase}/api/gpt/custom-prompt/${numericId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          access_token: token,
        },
        body: JSON.stringify({
          title: editCustomTitle.trim(),
          content: editCustomContent,        // 서버에서 trim 검사하므로 그대로 전송해도 OK
          guideline: editCustomGuideline,
          isActive: editCustomIsActive,
        }),
      });
  
      // 서버 표준 응답 처리
      const data = await res.json().catch(() => ({} as any));
      if (!res.ok || !data?.success) {
        // 백엔드 메시지에 맞춰 상세 안내
        const msg =
          data?.message ||
          (res.status === 400 ? '요청 형식이 올바르지 않습니다.' :
           res.status === 404 ? '커스텀 프롬포트를 찾을 수 없습니다.' :
           res.status === 409 ? '같은 제목의 커스텀 AI 설정이 이미 존재합니다.' :
           `수정 실패: HTTP ${res.status}`);
        throw new Error(msg);
      }
  
      await fetchCustomPrompts(); // 최신 목록 반영
      toast.success('커스텀 AI가 수정되었습니다.');
      setEditDirty(false);
    } catch (error: any) {
      console.error('커스텀 프롬프트 수정 오류:', error);
      toast.error(`커스텀 프롬프트 수정 실패: ${error.message || '알 수 없는 오류'}`);
    } finally {
      setSaving(false);
    }
  };

  // 편집폼 초기화(현재 선택된 커스텀 값으로 되돌림)
  const resetCustomPromptEdits = () => {
    if (!selectedCustomPrompt) return;
    setEditCustomTitle(selectedCustomPrompt.title || '');
    setEditCustomContent(selectedCustomPrompt.content || '');
    setEditCustomGuideline(selectedCustomPrompt.guideline || '');
    setEditCustomIsActive(
      typeof selectedCustomPrompt.isActive === 'boolean'
        ? selectedCustomPrompt.isActive
        : true,
    );
    setEditDirty(false);
  };

  // 선택된 프롬프트 타입의 표시명 반환
  const getSelectedTypeDisplayName = (): string => {
    if (defaultPromptType === 'diary') return '일기형';
    if (defaultPromptType === 'promotion') return '홍보형';
    if (defaultPromptType.startsWith('custom_')) {
      const customPrompt = customPrompts.find(
        (p) => `custom_${p.id}` === defaultPromptType,
      );
      return customPrompt ? `커스텀: ${customPrompt.title}` : '커스텀 AI 설정';
    }
    return '알 수 없음';
  };

  return (
    <Box>
      {/* 헤더 */}
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant={isMobile ? 'body1' : 'h6'}>AI 설정</Typography>
      </Box>

      {/* 기본 AI 프롬프트 설정 */}
      <Card sx={{ mb: 3, bgcolor: '#f8f9fa' }}>
        <CardContent>
          <Typography
            variant={isMobile ? 'body1' : 'h6'}
            gutterBottom
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <Check color='primary' />
            AI 프롬프트 타입 선택
          </Typography>
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{ mb: 3, fontSize: isMobile ? '0.8rem' : '0.875rem' }}
          >
            AI 포스팅 생성 시 사용할 프롬프트 타입을 선택하세요. 커스텀 AI를
            만들어서 나만의 스타일을 설정할 수도 있습니다.
          </Typography>

          <Stack spacing={3}>
            <FormControl fullWidth>
              <InputLabel
                shrink
                sx={{ fontSize: isMobile ? '0.9rem' : '1rem' }}
              >
                AI 프롬프트 타입 선택
              </InputLabel>
              <Select
                value={defaultPromptType}
                onChange={(e) => handlePromptTypeChange(e.target.value)}
                label='AI 프롬프트 타입 선택'
                notched
                sx={{
                  height: isMobile ? '48px' : '56px',
                  '& .MuiSelect-select': {
                    paddingTop: isMobile ? '12px' : '16px',
                    paddingBottom: isMobile ? '12px' : '16px',
                    display: 'flex',
                    alignItems: 'center',
                  },
                }}
              >
                <MenuItem value='diary'>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label='마켓팅'
                      color='primary'
                      size={isMobile ? 'small' : 'small'}
                      sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}
                    />
                    <Typography
                      sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}
                    >
                      마케팅 포스팅 스타일
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem value='promotion'>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label='사장님'
                      color='secondary'
                      size={isMobile ? 'small' : 'small'}
                      sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}
                    />
                    <Typography
                      sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}
                    >
                      사장님 입장에서 내 가게 홍보 스타일
                    </Typography>
                  </Box>
                </MenuItem>

                {/* 저장된 커스텀 AI들을 옵션으로 추가 */}
                {customPrompts.map((customPrompt) => (
                  <MenuItem
                    key={customPrompt.id}
                    value={`custom_${customPrompt.id}`}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        width: '100%',
                      }}
                    >
                      <Chip
                        label='커스텀'
                        color='success'
                        size={isMobile ? 'small' : 'small'}
                        sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}
                      />
                      <Typography
                        sx={{
                          flex: 1,
                          fontSize: isMobile ? '0.8rem' : '0.875rem',
                        }}
                      >
                        {customPrompt.title}
                      </Typography>
                      <IconButton
                        size='small'
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteCustomPrompt(customPrompt.id!);
                        }}
                        sx={{ color: 'error.main' }}
                      >
                        <Delete fontSize='small' />
                      </IconButton>
                    </Box>
                  </MenuItem>
                ))}

                {/* 새로운 커스텀 AI 설정 만들기 옵션 */}
                <MenuItem value='create_new_custom'>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Add color='success' />
                    <Typography
                      color='success.main'
                      fontWeight='bold'
                      sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}
                    >
                      새로운 커스텀 AI 설정 만들기
                    </Typography>
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            {/* 기본 설정인 경우 */}
            {(defaultPromptType === 'diary' ||
              defaultPromptType === 'promotion') && (
              <Box>
                <TextField
                  fullWidth
                  label='참고할 Instagram URL (선택사항)'
                  value={instagramUrl}
                  onChange={(e) => setInstagramUrl(e.target.value)}
                  placeholder='https://www.instagram.com/username/'
                  helperText='📸 참고하고 싶은 인스타그램 계정의 URL을 입력하면, AI가 해당 계정의 글쓰기 스타일을 분석하여 비슷한 스타일의 포스팅을 생성합니다.'
                  error={
                    !isValidInstagramUrl(instagramUrl) && instagramUrl !== ''
                  }
                  sx={{
                    '& .MuiInputBase-root': {
                      height: isMobile ? '48px' : '56px',
                      '& input': {
                        fontSize: isMobile ? '0.9rem' : '1rem',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: isMobile ? '0.9rem' : '1rem',
                    },
                    '& .MuiFormHelperText-root': {
                      fontSize: isMobile ? '0.75rem' : '0.875rem',
                    },
                  }}
                />

                <Button
                  variant='contained'
                  startIcon={<Save />}
                  onClick={saveDefaultPrompts}
                  disabled={saving}
                  sx={{ alignSelf: 'flex-start', mt: 2, color: '#fff' }}
                >
                  {saving ? '저장 중...' : '기본 설정 저장'}
                </Button>
              </Box>
            )}

            {/* 커스텀 AI가 선택된 경우 */}
            {defaultPromptType.startsWith('custom_') && (
              <Box
                sx={{
                  p: 3,
                  bgcolor: '#f0f8ff',
                  borderRadius: 2,
                  border: '2px solid #e3f2fd',
                }}
              >
                <Typography
                  variant={isMobile ? 'body1' : 'h6'}
                  gutterBottom
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <Check color='success' />
                  선택된 커스텀 AI:{' '}
                  {selectedCustomPrompt?.title || '(로딩/없음)'}
                </Typography>
                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{ mb: 2 }}
                >
                  이 커스텀 AI를 기본 사용으로 지정하고, 아래에서
                  제목/내용/가이드라인, 활성화 여부를 수정할 수 있습니다.
                </Typography>
                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    label='제목'
                    value={editCustomTitle}
                    onChange={(e) => {
                      setEditCustomTitle(e.target.value);
                      setEditDirty(true);
                    }}
                    sx={{
                      '& .MuiInputBase-root': {
                        height: isMobile ? '48px' : '56px',
                      },
                    }}
                  />

                  <TextField
                    fullWidth
                    multiline
                    rows={6}
                    label='내용 (프롬프트)'
                    value={editCustomContent}
                    onChange={(e) => {
                      setEditCustomContent(e.target.value);
                      setEditDirty(true);
                    }}
                    placeholder='예: 톤/스타일/구성 지시문 등'
                    sx={{
                      '& .MuiInputBase-root': {
                        minHeight: '160px',
                        whiteSpace: 'pre-wrap',
                        '& textarea': {
                          paddingTop: '14px !important',
                          paddingBottom: '14px !important',
                        },
                      },
                    }}
                  />

                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label='가이드라인 (선택)'
                    value={editCustomGuideline}
                    onChange={(e) => {
                      setEditCustomGuideline(e.target.value);
                      setEditDirty(true);
                    }}
                    placeholder='예: 금지어, 길이 제한, CTA 규칙 등'
                  />

                  <FormControlLabel
                    control={
                      <Switch
                        checked={editCustomIsActive}
                        onChange={(e) => {
                          setEditCustomIsActive(e.target.checked);
                          setEditDirty(true);
                        }}
                      />
                    }
                    label={editCustomIsActive ? '활성화됨' : '비활성화됨'}
                  />

                  <Stack direction='row' spacing={1} sx={{ flexWrap: 'wrap' }}>
                    <Button
                      variant='contained'
                      startIcon={<Save />}
                      onClick={saveCustomPromptEdits}
                      disabled={
                        saving ||
                        !selectedCustomPrompt ||
                        !editCustomTitle.trim() ||
                        !editCustomContent.trim()
                      }
                      sx={{ color: '#fff' }}
                    >
                      {saving ? '수정 저장 중...' : '커스텀 프롬프트 수정 저장'}
                    </Button>

                    <Button
                      variant='outlined'
                      startIcon={<Refresh />}
                      onClick={resetCustomPromptEdits}
                      disabled={!selectedCustomPrompt}
                      sx={{ color: '#000' }}
                    >
                      변경 취소(되돌리기)
                    </Button>

                    {/* 기본값 저장(현재 선택을 기본으로 쓰기) */}
                    <Button
                      variant='outlined'
                      onClick={saveDefaultPrompts}
                      disabled={saving}
                      sx={{ color: '#000' }}
                    >
                      기본 사용 설정 저장
                    </Button>
                  </Stack>

                  {editDirty && (
                    <Alert severity='info'>
                      아직 저장하지 않은 변경사항이 있습니다.
                    </Alert>
                  )}
                </Stack>
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* 새로운 커스텀 AI 설정 만들기 폼 (조건부 표시) */}
      {showCustomPromptForm && (
        <Card sx={{ mb: 3, bgcolor: '#f0f8ff', border: '3px solid #2196f3' }}>
          <CardContent>
            <Typography
              variant='h6'
              gutterBottom
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <Add color='success' />
              새로운 커스텀 AI 설정 만들기
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
              나만의 AI 프롬프트를 만들어보세요. 원하는 스타일과 톤으로 포스팅을
              생성할 수 있습니다.
            </Typography>

            <Stack spacing={3}>
              <TextField
                fullWidth
                label='커스텀 AI 제목'
                value={customPromptTitle}
                onChange={(e) => setCustomPromptTitle(e.target.value)}
                placeholder='예: 친근한 동네 미용실 스타일'
                helperText='커스텀 AI를 구분할 수 있는 제목을 입력하세요.'
                sx={{
                  '& .MuiInputBase-root': {
                    height: '56px',
                    '& input': {
                      paddingTop: '14px',
                      paddingBottom: '14px',
                    },
                  },
                }}
              />

              <TextField
                fullWidth
                multiline
                rows={6}
                label='커스텀 AI 프롬프트 내용'
                value={customPromptContent}
                onChange={(e) => setCustomPromptContent(e.target.value)}
                placeholder='예: 친근하고 따뜻한 말투로 고객과의 소통을 중시하는 동네 미용실의 일상을 자연스럽게 담아주세요. 고객의 만족도와 변화된 모습을 중심으로 진솔하게 작성하며, 전문성보다는 친근함을 강조해주세요.'
                helperText='AI가 포스팅을 생성할 때 참고할 상세한 지침을 입력하세요.'
                sx={{
                  '& .MuiInputBase-root': {
                    minHeight: '160px',
                    whiteSpace: 'pre-wrap',
                    '& textarea': {
                      paddingTop: '14px !important',
                      paddingBottom: '14px !important',
                      whiteSpace: 'pre-wrap',
                    },
                  },
                }}
              />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant='contained'
                  color='success'
                  startIcon={<Save />}
                  onClick={saveCustomPrompt}
                  disabled={
                    saving ||
                    !customPromptTitle.trim() ||
                    !customPromptContent.trim()
                  }
                  sx={{ color: '#fff' }}
                  style={{ color: 'black' }}
                >
                  {saving ? '저장 중...' : '커스텀 AI 설정 저장'}
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* 현재 설정 상태 표시 */}
      <Card sx={{ mb: 3, bgcolor: '#f9f9f9' }}>
        <CardContent>
          <Typography
            variant='h6'
            gutterBottom
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <Check color='info' />
            현재 AI 설정 상태
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Chip
              label={`현재 타입: ${getSelectedTypeDisplayName()}`}
              color={
                defaultPromptType === 'diary'
                  ? 'primary'
                  : defaultPromptType === 'promotion'
                    ? 'secondary'
                    : 'success'
              }
              variant='filled'
            />
            {customPrompts.length > 0 && (
              <Chip
                label={`저장된 커스텀 AI: ${customPrompts.length}개`}
                color='success'
                variant='outlined'
              />
            )}
            {instagramUrl && (
              <Chip
                label='Instagram URL 설정됨'
                color='info'
                variant='outlined'
              />
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default GPTPromptSettings;
